<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert, useTrack } from 'dashboard/composables';
import { CAMPAIGN_TYPES } from 'shared/constants/campaign.js';
import { CAMPAIGNS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events.js';

import WhatsAppCampaignForm from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignForm.vue';

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const CAMPAIGN_TYPE_MESSAGE = 'message';
const CAMPAIGN_TYPE_CALL = 'call';

const selectedCampaignType = ref(null);
const campaignTypeOptions = computed(() => [
  {
    value: CAMPAIGN_TYPE_MESSAGE,
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.CAMPAIGN_TYPE.MESSAGE.LABEL'),
    description: t(
      'CAMPAIGN.WHATSAPP.CREATE.FORM.CAMPAIGN_TYPE.MESSAGE.DESCRIPTION'
    ),
  },
  {
    value: CAMPAIGN_TYPE_CALL,
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.CAMPAIGN_TYPE.CALL.LABEL'),
    description: t(
      'CAMPAIGN.WHATSAPP.CREATE.FORM.CAMPAIGN_TYPE.CALL.DESCRIPTION'
    ),
  },
]);

const addCampaign = async campaignDetails => {
  try {
    await store.dispatch('campaigns/create', campaignDetails);

    useTrack(CAMPAIGNS_EVENTS.CREATE_CAMPAIGN, {
      type: CAMPAIGN_TYPES.ONE_OFF,
      channel: 'whatsapp',
      whatsapp_campaign_type:
        campaignDetails.whatsapp_campaign_type || 'message',
      has_call_audio: Boolean(campaignDetails.call_audio_blob_id),
    });

    useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.API.SUCCESS_MESSAGE'));
    return true;
  } catch (error) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.response?.data?.errors?.join(', ') ||
      error?.response?.message ||
      t('CAMPAIGN.WHATSAPP.CREATE.FORM.API.ERROR_MESSAGE');
    useAlert(errorMessage);
    return false;
  }
};

const handleSubmit = async campaignDetails => {
  const campaignCreated = await addCampaign(campaignDetails);
  if (campaignCreated) handleClose();
};

const selectCampaignType = campaignType => {
  selectedCampaignType.value = campaignType;
  useTrack('Selected WhatsApp campaign type', { campaign_type: campaignType });
};

const handleBack = () => {
  selectedCampaignType.value = null;
};

const handleClose = () => emit('close');
</script>

<template>
  <div
    class="w-[calc(100vw-2rem)] sm:w-[25rem] z-50 min-w-0 absolute top-10 ltr:right-0 rtl:left-0 bg-n-alpha-3 backdrop-blur-[100px] rounded-xl border border-n-weak shadow-md max-h-[80vh] overflow-y-auto"
  >
    <div class="p-6 flex flex-col gap-6">
      <h3 class="text-base font-medium text-n-slate-12 flex-shrink-0">
        {{ t(`CAMPAIGN.WHATSAPP.CREATE.TITLE`) }}
      </h3>
      <template v-if="!selectedCampaignType">
        <p class="text-sm text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.CAMPAIGN_TYPE.QUESTION') }}
        </p>
        <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <button
            v-for="option in campaignTypeOptions"
            :key="option.value"
            type="button"
            class="flex flex-col gap-1 p-3 text-left transition-colors border rounded-lg border-n-weak hover:bg-n-alpha-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
            :data-test="`whatsapp-campaign-type-${option.value}`"
            @click="selectCampaignType(option.value)"
          >
            <span class="text-sm font-medium text-n-slate-12">
              {{ option.label }}
            </span>
            <span class="text-xs text-n-slate-11">{{
              option.description
            }}</span>
          </button>
        </div>
      </template>
      <WhatsAppCampaignForm
        v-else
        :campaign-type="selectedCampaignType"
        @submit="handleSubmit"
        @cancel="handleClose"
        @back="handleBack"
      />
    </div>
  </div>
</template>
